import { Component, OnInit, inject, ChangeDetectionStrategy, signal, input } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import {SabrService} from '../services/sabr-service';
import {SabrInvoice} from './sabr-invoice';
import {SabrRelease} from './sabr-release';
import {SabrSummary} from './sabr-summary';
import { TooltipPosition, MatTooltip } from '@angular/material/tooltip';
import {PersonLoginService} from '../services/person-login-service';
import {FileUtils} from "../utils/file-utils";
import { SortableColumnComponent } from '../sorting/sortable-column.component';
import { SortableTableDirective } from '../sorting/sortable-table.directive';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { CurrencyPipe, DatePipe } from '@angular/common';
import {StringUtils} from "../utils/string-utils";

@Component({
    selector: 'sabr',
    templateUrl: './sabr.component.html',
    styleUrls: ['./sabr.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MatProgressSpinner, MatTooltip, RouterLink, SortableTableDirective, SortableColumnComponent, CurrencyPipe, DatePipe]
})
export class SabrComponent implements OnInit {
  readonly folderNumber = input.required<string>();
  readonly entityId = input.required<string>();
  readonly customerId = input.required<string>();
  private readonly router = inject(Router);
  private readonly sabrService = inject(SabrService);
  private readonly personLoginService = inject(PersonLoginService);


  readonly isBusy = signal<boolean>(undefined!);
  readonly recordFound = signal<boolean>(undefined!);
  readonly errorMessage = signal<string>(undefined!);
  readonly sabrSummary = signal<SabrSummary>(undefined!);
  readonly projectAmounts = signal<string[]>([]);
  positionOptions: TooltipPosition[] = ['left', 'right'];

  constructor
  () {
    this.sabrSummary.set(new SabrSummary());
  }

  private handleError(error: any): void {
    this.errorMessage.set(StringUtils.getErrorMessage(error));
    this.isBusy.set(false);
  }

  onSortedReleases($event: any) {
    this.reSortReleases($event);
  }

  onSortedInvoices($event: any) {
    this.reSortInvoices($event);
  }

  ngOnInit(): void {
    if (this.personLoginService.isOutsideEngineer()) {
      this.router.navigate(['ratings']);
    } else {
      this.isBusy.set(true);
      this.recordFound.set(false);
      {
        const folderNumber: string = this.folderNumber();
        const entityId: string = this.entityId();
        const customerId: string = this.customerId();
        this.sabrService.getSabrSummary(folderNumber, entityId, customerId).subscribe(result => {
          this.sabrSummary.set(result);
          if (this.sabrSummary() == null) {
            this.errorMessage.set("Sabr Summary information was not found");
          } else {
            this.recordFound.set(true);
            this.sortReleases();
            this.sortInvoices();
            this.projectAmounts.set([this.sabrSummary().unreleasedAmount.toFixed(2),
              this.sabrSummary().releasedInProcessAmount.toFixed(2),
              this.sabrSummary().releasedShippedAmount.toFixed(2)]);
          }
        });
      }
      this.isBusy.set(false);
    }
  }

  getTotalReleasedQuantity(releases: SabrRelease[]): number {
    return releases.map(release => release.quantity).reduce((accumulator, value) => accumulator + value);
  }

  getTotalReleasedAmount(releases: SabrRelease[]): number {
    return releases.map(release => release.amount).reduce((accumulator, value) => accumulator + value);
  }

  getTotalReleaseInvoiceQuantity(releases: SabrRelease[]): number {
    return releases.map(release => release.invoiceQuantity).reduce((accumulator, value) => accumulator + value);
  }

  getTotalReleaseInvoiceAmount(releases: SabrRelease[]): number {
    return releases.map(release => release.invoiceAmount).reduce((accumulator, value) => accumulator + value);
  }

  getTotalRemainingQuantity(releases: SabrRelease[]): number {
    return this.getTotalReleasedQuantity(releases) - this.getTotalReleaseInvoiceQuantity(releases);
  }

  getTotalRemainingAmount(releases: SabrRelease[]): number {
    return this.getTotalReleasedAmount(releases) - this.getTotalReleaseInvoiceAmount(releases);
  }

  getTotalInvoiceQuantity(invoices: SabrInvoice[]): number {
    return invoices.map(invoice => invoice.quantity).reduce((accumulator, value) => accumulator + value);
  }

  getTotalInvoiceAmount(invoices: SabrInvoice[]): number {
    return invoices.map(invoice => invoice.amount).reduce((accumulator, value) => accumulator + value);
  }

  sortReleases(): void {
    this.sabrSummary().releases.sort(function (a, b) {
      if ((new Date(a.scheduleDate).getTime() - new Date(b.scheduleDate).getTime()) === 0) {
        if ((a.erpOrder - b.erpOrder) === 0) {
          return 0;
        } else {
          return a.erpOrder - b.erpOrder;
        }
      } else {
        return new Date(a.scheduleDate).getTime() - new Date(b.scheduleDate).getTime();
      }
      return 0;
    });
  }

  sortInvoices(): void {
    this.sabrSummary().invoices.sort(function (a, b) {
      if ((new Date(a.shipDate).getTime() - new Date(b.shipDate).getTime()) === 0) {
        if ((a.invoiceNumber - b.invoiceNumber) === 0) {
          return 0;
        } else {
          return a.invoiceNumber - b.invoiceNumber;
        }
      } else {
        return new Date(a.shipDate).getTime() - new Date(b.shipDate).getTime();
      }
      return 0;
    });
  }

  reSortReleases(criteria: SortCriteria): SabrRelease[] {
    if (criteria.sortDirection === 'desc') {
      return this.sabrSummary().releases.sort(function (a, b) {
        if (criteria.sortColumn === 'release.erpOrder') {
          return b.erpOrder - a.erpOrder;
        } else if (criteria.sortColumn === 'release.encompassReleaseNumber') {
          return b.encompassReleaseNumber.localeCompare(a.encompassReleaseNumber)
        } else if (criteria.sortColumn === 'release.scheduleDate') {
          return new Date(b.scheduleDate).getTime() - new Date(a.scheduleDate).getTime();
        }
        return 0;
      });
    } else {
      return this.sabrSummary().releases.sort(function (a, b) {
        if (criteria.sortColumn === 'release.erpOrder') {
          return a.erpOrder - b.erpOrder;
        } else if (criteria.sortColumn === 'release.encompassReleaseNumber') {
          return a.encompassReleaseNumber.localeCompare(b.encompassReleaseNumber)
        } else if (criteria.sortColumn === 'release.scheduleDate') {
          return new Date(a.scheduleDate).getTime() - new Date(b.scheduleDate).getTime();
        }
        return 0;
      });
    }
  }

  reSortInvoices(criteria: SortCriteria): SabrInvoice[] {
    if (criteria.sortDirection === 'desc') {
      return this.sabrSummary().invoices.sort(function (a, b) {
        if (criteria.sortColumn === 'invoice.shipDate') {
          return new Date(b.shipDate).getTime() - new Date(a.shipDate).getTime();
        } else if (criteria.sortColumn === 'invoice.orderNumber') {
          return b.orderNumber - a.orderNumber;
        } else if (criteria.sortColumn === 'invoice.invoiceNumber') {
          return b.invoiceNumber - a.invoiceNumber;
        }
        return 0;
      });
    } else {
      return this.sabrSummary().invoices.sort(function (a, b) {
        if (criteria.sortColumn === 'invoice.shipDate') {
          return new Date(a.shipDate).getTime() - new Date(b.shipDate).getTime();
        } else if (criteria.sortColumn === 'invoice.orderNumber') {
          return a.orderNumber - b.orderNumber;
        } else if (criteria.sortColumn === 'invoice.invoiceNumber') {
          return a.invoiceNumber - b.invoiceNumber;
        }
        return 0;
      });
    }
  }

  public getInvoiceDocument(invoiceNumber: number) {
    this.isBusy.set(true);
    this.errorMessage.set(undefined!);
    this.sabrService.downloadInvoiceDocument('IEC', invoiceNumber).subscribe(response => {
      this.isBusy.set(false);
      FileUtils.downloadFile(response, invoiceNumber + '.pdf');
    }, error => {
      this.handleError(error);
    });
  }

}

export class SortCriteria {
  sortColumn!: string;
  sortDirection!: string;
}
